import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import OptimizedImage from "@/components/OptimizedImage";
import { useSignIn } from "@/hooks/useSignIn";

interface HowItWorksProps {
  onSignIn: () => void;
}

const HowItWorks: React.FC<HowItWorksProps> = ({ onSignIn }) => {
  const { signingIn } = useSignIn();
  
  const handleClick = async () => {
    if (signingIn) return;
    await onSignIn();
  };
  
  return (
    <section id="how-it-works" className="w-full pt-4 sm:pt-6 lg:pt-8 pb-12 sm:pb-16 lg:pb-20 bg-muted/20 scroll-mt-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center sm:text-left mb-4 sm:mb-6">
            <h1 className="text-xs sm:text-sm md:text-base font-bold text-foreground/90 uppercase tracking-wider">
              HOW IT WORKS
            </h1>
          </div>
          <div className="text-center sm:text-left mb-12 sm:mb-16 md:mb-20">
            <div className="flex flex-col lg:flex-row items-center sm:items-start lg:items-end gap-6 lg:gap-24">
              <div className="flex items-end gap-0 w-full lg:w-auto justify-center sm:justify-start">
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                  From zero to <span className="text-primary">ranking</span><br />
                  <span className="inline-flex items-end gap-2 sm:gap-3 md:gap-4">
                    in three steps
                    <OptimizedImage
                      src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/arrow-right-black.538c912a.svg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2Fycm93LXJpZ2h0LWJsYWNrLjUzOGM5MTJhLnN2ZyIsImlhdCI6MTc2Mzg1MTIwOSwiZXhwIjoxOTIxNTMxMjA5fQ.bBnvaFY4fcPXFcUI3k4ZcGcVmjjZsJVbgvuLZpQNQXI"
                      alt=""
                      width={24}
                      height={24}
                      className="h-3 sm:h-4 md:h-5 lg:h-6 w-auto hidden sm:block flex-shrink-0 mb-1 sm:mb-1.5"
                      aria-hidden="true"
                    />
                  </span>
                </h2>
              </div>
              <div className="flex flex-col items-center sm:items-start gap-2 lg:gap-1 w-full lg:w-auto lg:-mt-8">
                <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl text-center sm:text-left mb-0 lg:transform lg:-translate-y-5">
                  Stop struggling with SEO. We analyze, create, and publish ranking content that drives traffic—so you can focus on what matters: growing your business.
                </p>
              </div>
            </div>
          </div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-16 mb-12 sm:mb-16">
            {/* Step 1 */}
            <ScrollReveal direction="up" delay={0}>
              <div className="flex flex-col">
              <div className="bg-background rounded-3xl p-4 sm:p-6 md:p-8 shadow-lg border border-border/50 transition-shadow mb-4 sm:mb-6">
                <OptimizedImage 
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/analyze%20your%20website%20illustration2%20.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2FuYWx5emUgeW91ciB3ZWJzaXRlIGlsbHVzdHJhdGlvbjIgLnBuZyIsImlhdCI6MTc2NDAxMDU5MywiZXhwIjoxOTIxNjkwNTkzfQ.bASBfX50uyOcQ6TL4wEi_cn1xyOhddhq_S8rEkJVzFI" 
                  alt="Deep analysis of your business" 
                  width={600}
                  height={400}
                  className="w-full rounded-2xl object-contain"
                />
                  </div>
              <div className="text-left">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground mb-2 sm:mb-3">
                    Deep analysis of your business
                  </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    We analyze your niche, competitors, and audience to uncover high-opportunity keywords. Get a complete content strategy tailored to your business—no guesswork required.
                  </p>
              </div>
              </div>
            </ScrollReveal>

            {/* Step 2 */}
            <ScrollReveal direction="up" delay={75}>
              <div className="flex flex-col">
              <div className="bg-background rounded-3xl p-4 sm:p-6 md:p-8 shadow-lg border border-border/50 transition-shadow mb-4 sm:mb-6">
                <OptimizedImage 
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/articles%20showcase%202.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2FydGljbGVzIHNob3djYXNlIDIucG5nIiwiaWF0IjoxNzY0MDEwMzc2LCJleHAiOjE5MjE2OTAzNzZ9.CrBfMyxrNw924PSdXBcDcFrL902gbjTTqsk12h-TGVk" 
                  alt="Generate content that ranks" 
                  width={600}
                  height={400}
                  className="w-full rounded-2xl object-contain"
                />
              </div>
              <div className="text-left">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground mb-2 sm:mb-3">
                  Generate content that ranks
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Get 30 topic clusters monthly with ready-to-publish articles. One click generates SEO-optimized content that ranks—complete with images, internal links, and keyword optimization.
                  </p>
              </div>
              </div>
            </ScrollReveal>

            {/* Step 3 */}
            <ScrollReveal direction="up" delay={150}>
              <div className="flex flex-col">
              <div className="bg-background rounded-3xl p-4 sm:p-6 md:p-8 shadow-lg border border-border/50 transition-shadow mb-4 sm:mb-6">
                <OptimizedImage 
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/sign/random%20images/backlins%20image%202.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8xOWQwMmZkMy05MzllLTQ3YWYtODEyYS0zOWQxMTc0NGY2N2IiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJyYW5kb20gaW1hZ2VzL2JhY2tsaW5zIGltYWdlIDIucG5nIiwiaWF0IjoxNzY0MDE3NTk1LCJleHAiOjE5MjE2OTc1OTV9.fpgNlb_106lAev2mdGp_wP8fu88zBHeH-ZWLtw4K5Kg" 
                  alt="Boost authority with backlinks" 
                  width={600}
                  height={400}
                  className="w-full rounded-2xl object-contain"
                />
              </div>
              <div className="text-left">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground mb-2 sm:mb-3">
                  Boost authority with backlinks
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Get 10 premium backlinks (DA 70+) delivered monthly. Watch your domain authority climb and your rankings improve—all on autopilot.
                </p>
              </div>
              </div>
            </ScrollReveal>
          </div>

          {/* CTA Section */}
          <ScrollReveal direction="up" delay={225}>
            <div className="text-center">
            <Button
              onClick={handleClick}
              disabled={signingIn}
              size="lg"
              className="w-full sm:w-auto rounded-full text-lg px-8 py-6 font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg min-h-[60px] relative overflow-hidden group mx-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
            >
                              <span className="relative z-10 font-semibold tracking-wide">Get Started Now</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
            </Button>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
